import React, { useCallback, useEffect, useState } from 'react';

import { ProgressSnapshot, getLearnerProgress } from '../../api/learner';
import { KidEmpty } from '../../../../design-system/kid';
import { useLearnerSpace } from '../../learner/LearnerSpace';
import { DisplayTimeZone } from '../../api/learner';
import { formatDay, useLearnerTimeZone } from '../../learner/learnerTime';
import {
  milestonesShown,
  progressModeOf,
  showsLearnerState,
  showsTeacherFeedback,
  observationLineOf,
} from '../../learner/progressWords';

type Load =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; snapshots: ProgressSnapshot[] };

/**
 * UC-S11 — "Mi progreso" (STU-13-FR-01..05, 09).
 *
 * Renders the shared progress projection and computes nothing: the same
 * snapshot the parent reads, with its version and as-of shown so the two can
 * be compared (FR-09, S11-CON-01). When there is not enough history the state
 * is "pendiente" — never 0%. LEP_0 and LEP_1 never show milestone counts or
 * anything that reads as a grade; LEP_2 and LEP_3 show milestones only when
 * the projection carries them, and say they are not a grade. Teacher feedback
 * is only the learner-shared part the projection exposes; until there is
 * some, the screen says it has not been written yet rather than inventing it.
 *
 * MC01-R10: a section the course's mode does not have is not drawn at all.
 * HISTORY_ONLY (LEP_0) has no teacher's note and no teacher comment, so neither
 * appears as "pendiente" or "todavía no"; SERVICE_ONLY (a course with no
 * profile yet) shows only the classes the teacher gave.
 */
const MyProgress: React.FC = () => {
  const { studentId } = useLearnerSpace();
  const tz = useLearnerTimeZone(studentId);
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  const fetchProgress = useCallback(() => {
    if (!studentId) return;
    setLoad({ status: 'loading' });
    getLearnerProgress(studentId)
      .then((res) => setLoad({ status: 'ready', snapshots: res.snapshots }))
      .catch(() => setLoad({ status: 'error' }));
  }, [studentId]);

  useEffect(fetchProgress, [fetchProgress]);

  return (
    <>
      <h1 className="hi">Mi progreso</h1>
      <p className="hi-s">Es lo mismo que ve tu familia.</p>

      {load.status === 'loading' && (
        <p className="hi-s" role="status">
          Cargando cómo vas…
        </p>
      )}

      {load.status === 'error' && (
        // S07-TR-04 — unavailable is said, not papered over with a last value.
        <div className="lrn-status" role="alert">
          <h2>Ahora no podemos mostrar cómo vas</h2>
          <p>No es culpa tuya. Prueba otra vez en un momento.</p>
          <button className="ag-b" onClick={fetchProgress}>
            Intentar otra vez
          </button>
        </div>
      )}

      {load.status === 'ready' && load.snapshots.length === 0 && (
        <KidEmpty
          icon="🌱"
          title="Todavía no hay nada que mostrar"
          detail="Cuando tengas clases, aquí verás cómo vas."
        />
      )}

      {load.status === 'ready' &&
        load.snapshots.map((s) => <ProgressCard key={s.enrollmentId} snapshot={s} tz={tz} />)}
    </>
  );
};

const ProgressCard: React.FC<{ snapshot: ProgressSnapshot; tz: DisplayTimeZone }> = ({
  snapshot,
  tz,
}) => {
  const notEnough = snapshot.dataSufficiency === 'NOT_ENOUGH_HISTORY';
  const milestones = milestonesShown(snapshot);
  const serviceOnly = progressModeOf(snapshot) === 'SERVICE_ONLY';
  const withState = showsLearnerState(snapshot);
  const attended = snapshot.sessionsAttended ?? 0;
  const given = snapshot.sessionsDelivered ?? 0;

  return (
    <section className="lrn-status" aria-label={snapshot.courseTitle}>
      <h2>{snapshot.courseTitle}</h2>

      {withState && <p>{observationLineOf(snapshot)}</p>}
      {withState && notEnough && <p>No es un fallo ni es culpa tuya.</p>}

      {serviceOnly ? (
        <p>
          {snapshot.sessionsTotal !== undefined
            ? `Tu profesor dio ${given} de ${snapshot.sessionsTotal} clases.`
            : `Tu profesor dio ${given} ${given === 1 ? 'clase' : 'clases'}.`}
        </p>
      ) : (
        <p>
          {snapshot.sessionsTotal !== undefined
            ? `Fuiste a ${attended} de ${snapshot.sessionsTotal} clases.`
            : `Fuiste a ${attended} ${attended === 1 ? 'clase' : 'clases'}.`}
        </p>
      )}
      {!serviceOnly && !!snapshot.sessionsMissed && (
        // STU-13-FR-04 — said plainly, never as a fault. It is not in the
        // count above: a class the learner missed is not progress (CHG-002).
        <p>
          {`Te perdiste ${snapshot.sessionsMissed} ${
            snapshot.sessionsMissed === 1 ? 'clase' : 'clases'
          }. No pasa nada: te esperamos en la próxima.`}
        </p>
      )}

      {milestones && (
        <p>
          {milestones.completed} de {milestones.total} metas cumplidas. Las metas no son una nota.
        </p>
      )}

      {showsTeacherFeedback(snapshot) && (
        <>
          <h3 className="h2">Lo que dice tu profesor</h3>
          {snapshot.latestLearnerFeedback ? (
            <blockquote className="note">{snapshot.latestLearnerFeedback.text}</blockquote>
          ) : (
            <p>Tu profesor todavía no lo ha escrito.</p>
          )}
        </>
      )}

      <p className="lrn-policy">
        Actualizado el {formatDay(snapshot.asOf, tz)}
        {tz.source === 'UTC' ? ' (UTC)' : ''} · versión {String(snapshot.projectionVersion)}
      </p>
    </section>
  );
};

export default MyProgress;
